import { useEffect, useMemo, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { AddToHomePageModal, AddToHomeScreenButton } from '@appcharge/shared-ui';
import { useSaveToHomePage } from 'hooks/useSaveToHomePage';
import useStoreAppearance from 'hooks/useStoreAppearance';
import { EEventsType, EPlatformType, EStorePhase } from 'constants/enums';
import { BootResponse } from 'constants/apiResponses.types';
import useCustomEvents from 'hooks/useCustomEvents';
import useApi from 'hooks/useApi';
import { getPlatformData } from 'utils';

export const AddToHomePage = ({ className = '', ...props }: { className?: string }) => {
  const API = useApi({});
  const publisherMetaData = API.getPublisherMeta.data as BootResponse;
  const customEvents = useCustomEvents();

  const [devicePlatform, setDevicePlatform] = useState(EPlatformType.WEB);
  const {
    isSaveToHomePageOpen,
    setIsSaveToHomePageOpen,
    handleCloseSaveToHomePage,
    isDisplayAddToHomePageButton,
    handleInstallApp,
    isAndroidFallBack,
  } = useSaveToHomePage();
  const isDarkMode = useStoreAppearance();
  const { t } = useTranslation();

  useEffect(() => {
    if (isIOS) {
      setDevicePlatform(EPlatformType.IOS);
    }
    if (isAndroid) {
      setDevicePlatform(EPlatformType.ANDROID);
    }
  }, [isAndroid, isIOS]);

  const isIOSBanner = isIOS && isSaveToHomePageOpen;
  const isAndroidBanner = isAndroid && isAndroidFallBack;

  const displayHomeScreenGuide = useMemo(() => {
    return isIOSBanner || isAndroidBanner;
  }, [isIOSBanner, isAndroidBanner]);

  useEffect(() => {
    if (displayHomeScreenGuide) {
      customEvents.sendCustomEvent(
        EEventsType.HOME_SCREEN_GUIDE_PRESENTED,
        {
          platform: getPlatformData(),
        },
        EStorePhase.POST_LOGIN
      );
    }
  }, [displayHomeScreenGuide]);

  const handleAddToHomeScreen = () => {
    customEvents.sendCustomEvent(
      EEventsType.HOME_SCREEN_BUTTON_CLICK,
      {
        platform: devicePlatform,
      },
      EStorePhase.POST_LOGIN
    );

    if (isIOS) {
      setIsSaveToHomePageOpen(true);
    }
    if (isAndroid) {
      return handleInstallApp();
    }
  };

  if (!isDisplayAddToHomePageButton) {
    return null;
  }

  return (
    <div className={className}>
      <AddToHomeScreenButton
        onClick={handleAddToHomeScreen}
        borderColor={
          publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.borderColor?.colorOne
        }
        backgroundColor={
          publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.backgroundColor.colorOne
            ? publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.backgroundColor
            : undefined
        }
        buttonImage={publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.buttonImage}
        translations={{ addToHomeScreen: t('shop.addToHomeScreen') }}
        {...props}
      />
      {displayHomeScreenGuide && (
        <AddToHomePageModal
          iconImage={publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.iconImage}
          shortcutName={publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.shortcutName}
          isDarkMode={isDarkMode}
          onClick={handleCloseSaveToHomePage}
          isAndroid={isAndroidBanner}
          translations={{
            installInstruction: t('addToHomeModal.installInstruction', {
              companyName: publisherMetaData.storeTheme.storeScreen.addToHomeScreen?.shortcutName,
            }),
            description: t('addToHomeModal.description'),
            moreInfoStep: t('addToHomeModal.moreInfoStep'),
            shareStep: t('addToHomeModal.shareStep'),
            addStep: t('addToHomeModal.addStep'),
          }}
          {...props}
        />
      )}
    </div>
  );
};
